import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { ProfileLevel } from '@enums/acl/resources.enum';
import { ChatReadStatus } from '@enums/chat-read-status.enum';
import { ChatStatusE } from '@enums/chat-status.enum';
import { SenderTypeE } from '@enums/message.enum';
import { environment } from '@environments/environment';
import { UserHelperService } from '@helpers/user-helper.service';
import { ChatFilterI } from '@interfaces/chat-filter.interface';
import { ChatDynamicFieldI, ChatGroupI, ChatI, ChatMentionI, ChatStepI, InternalChatI } from '@interfaces/chat.interface';
import { MessagesInteractionE } from '@interfaces/flow.interface';
import { MailHistoryI } from '@interfaces/mail-history.interface';
import { MessageI } from '@interfaces/message.interface';
import { PageI } from '@interfaces/page.interface';
import { TabulationI } from '@interfaces/tabulation.interface';
import { UserSocketLoginI } from '@interfaces/user.interface';
import { ChatStatusT, transferChatT } from '@omnitypes/chat.type';
import { chatNotificationI } from '@store/chat/interface/chat-store.interface';
import { BehaviorSubject, EMPTY, firstValueFrom, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  public $toggleEmoji = new EventEmitter<boolean>();
  private messageChatSelected = new BehaviorSubject<MessageI | undefined>(undefined);

  private readonly CHAT_BASE_URL = environment.OMNISMART_API_CHAT_PERSISTENCE;
  private readonly ATTENDANCE_BASE_URL = environment.OMNISMART_API_ATTENDANCE_REPORTS;

  private slug_url_group_by_user = 'chat/group/by-user/';
  private slug_url_last_contact_by_user = 'chat/last-contact/by-user/';
  private slug_url_by_user = 'chat/by-user/';
  private slug_url_chats = 'chat/';
  private slug_url_chats_check_chats_unassigned = 'chat/check-chats-unassigned';
  private slug_url_by_contact = 'chat/by-contact/';
  private slug_url_contact = 'chat/contact/';
  private slug_url_sms = 'chat/send-sms/';
  private slug_url_email_history = 'chat/email-history/';
  private slug_url_messages = 'messages/';
  private slug_url_read_message = 'messages/update-all-messages-status/';
  private slug_url_filter_message = 'messages/filter/';
  private slug_url_start_transcription = 'messages/start-transcription';
  private slug_url_finish_chat = 'flow-finish-attendance/agent/';
  private slug_url_update_status_chat = 'update-status/';
  private slug_url_update_tabulation_commenter = 'update-tabulation-commenter/';
  private slug_url_send_tabulations_chat = 'send-tabulations-attendance/';
  private slug_url_send_commenter_chat = 'send-commenter-attendance/';
  private slug_url_update_dynamic_fields = 'update-dynamic-fields/';
  private slug_url_count_my_attendances = 'attendance-reports/agents-in-attendance';
  private slug_url_update_read_status_chat = 'update-read-status/';
  private slug_url_find_by_kanban_category = 'find-by-kanban-category/';
  private slug_url_update_chat_mentions = 'update-mentions/';
  private slug_url_signalize_integration = 'signalize-integration-by-contact-id/';
  private slug_unreaded_chats_count = 'chat/unreaded-chats-count';
  private slug_unreaded_chats_last_count = 'chat/unreaded-chats-count/last-contact';
  private slug_url_internal_chats = 'chat/internal-chats';
  private slug_url_attendance_pdf_exports = 'pdf-exports/';

  private ongoingRequestSubject = new BehaviorSubject<boolean>(false);
  public ongoingRequest$ = this.ongoingRequestSubject.asObservable();
  private finishedChatsCountSubject = new BehaviorSubject<{ total: number; maxFinishedAt: string } | null>(null);
  public finishedChatsCount$ = this.finishedChatsCountSubject.asObservable();

  constructor(private http: HttpClient, private readonly userHelperService: UserHelperService) {}

  public startTranscription(message: MessageI): Observable<MessageI> {
    return this.http.post<MessageI>(this.CHAT_BASE_URL + this.slug_url_start_transcription, message);
  }

  public endChat(chat: { _id: string }): Observable<any> {
    if (this.ongoingRequestSubject.getValue()) {
      return EMPTY;
    }
    this.ongoingRequestSubject.next(true);

    return this.http
      .post(environment.OMNISMART_API_FLOW_ENGINE + this.slug_url_finish_chat, chat)
      .pipe(tap(() => this.ongoingRequestSubject.next(false)));
  }

  public endManyChats(payload: {
    tabulations?: Array<{
      tabulationIds: string[];
      commentTabulation?: string;
      chatIds: string[];
    }>;
    chatIds: string[];
  }): Promise<{ jobId: string }> {
    return firstValueFrom(
      this.http.post<any>(environment.OMNISMART_API_FLOW_ENGINE + 'flow-finish-attendance/agent/multiple', payload)
    );
  }

  public sendTabulationsChat(
    chatId: string,
    data: { tabulationIds: string[]; commentTabulation: string }
  ): Observable<ChatStepI> {
    return this.http.patch<ChatStepI>(
      this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_send_tabulations_chat + chatId,
      data
    );
  }

  public sendCommenterChat(chatId: string, data: any): Observable<any> {
    return this.http.patch(this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_send_commenter_chat + chatId, data);
  }

  /**
   * Substitui o array de dynamicFields do chat. O endpoint faz replace do array
   * inteiro, então o chamador precisa enviar a lista completa (atuais + novos).
   */
  public updateChatDynamicFields(chatId: string, dynamicFields: ChatDynamicFieldI[]): Observable<ChatI> {
    return this.http.patch<ChatI>(
      this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_update_dynamic_fields + chatId,
      { dynamicFields }
    );
  }

  public archiveChat(chatId: string): Observable<any> {
    if (this.ongoingRequestSubject.getValue()) {
      return EMPTY;
    }
    this.ongoingRequestSubject.next(true);

    return this.http
      .patch(this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_update_status_chat + chatId, {
        status: 'archived',
      })
      .pipe(tap(() => this.ongoingRequestSubject.next(false)));
  }

  public unarchiveChat(chatId: string, status: ChatStatusE): Observable<any> {
    if (this.ongoingRequestSubject.getValue()) {
      return EMPTY;
    }
    this.ongoingRequestSubject.next(true);

    return this.http
      .patch(this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_update_status_chat + chatId, {
        status,
      })
      .pipe(tap(() => this.ongoingRequestSubject.next(false)));
  }

  public updateChatStatus(chatId: string, status: ChatStatusE): Observable<any> {
    return this.http.patch(this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_update_status_chat + chatId, {
      status: status,
    });
  }

  public signalizeIntegrationByContactId(id: string) {
    return this.http.post(this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_signalize_integration + id, {});
  }

  public updateChatTabulationAndCommenter(chatId: string, commenter: string, tabulationId: string): Observable<any> {
    return this.http.patch(
      this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_update_tabulation_commenter + chatId,
      {
        tabulationId,
        commentTabulation: commenter,
      }
    );
  }

  public updateChatReadStatus(chatId: string, readStatus: ChatReadStatus): Observable<any> {
    return this.http.patch(this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_update_read_status_chat + chatId, {
      readStatus,
    });
  }

  public findChatByContactNumberPhone(contact: string, customerId: string, phoneNumber: string) {
    return this.http.get<ChatI>(
      this.CHAT_BASE_URL +
        this.slug_url_by_contact +
        contact +
        `/phone-number/${phoneNumber}?customer=${customerId}&ignoreAnyFinalized=true`
    );
  }

  public findChatByChannelContactNumberPhone(
    contact: string,
    customerId: string,
    phoneNumber: string,
    channelId: string
  ) {
    return this.http.get<ChatI>(
      this.CHAT_BASE_URL +
        this.slug_url_contact +
        contact +
        /channel/ +
        channelId +
        `/phone-number/${phoneNumber}?customer=${customerId}&ignoreAnyFinalized=true`
    );
  }

  public getAllChatsByAllTeams(
    { team, customerId, _id }: UserSocketLoginI,
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI = {} as ChatFilterI,
    customSort?,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams()
      .set('team', team.toString())
      .set('userId', _id)
      .set('customer', customerId)
      .set('page', page)
      .set('limit', limit)
      .set('inQueue', false);

    if (search) {
      httpParams = httpParams.set('search', search);
    }
    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }
    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user, {
      params: httpParams,
    });
  }

  public async getUnreadedChatsCount(options?: {
    status?: ChatStatusE[];
    inQueue?: boolean;
  }): Promise<chatNotificationI[]> {
    const user = this.userHelperService.getSocketLoginData()!;
    let params = new HttpParams()
      .set('team', user.team.toString())
      .set('userId', user._id)
      .set('inQueue', !!options?.inQueue)
      .set('customer', user.customerId);

    if (Array.isArray(options?.status) && options?.status?.length) {
      options.status.forEach((status, index) => {
        params = params.set(`statusList[${index}]`, status);
      });
    }

    return firstValueFrom(
      this.http.get<chatNotificationI[]>(this.CHAT_BASE_URL + this.slug_unreaded_chats_count, { params })
    );
  }

  public async getUnreadedChatsLastContactCount(
    filter: ChatFilterI | any = {} as ChatFilterI,
    customSort?: any
  ): Promise<chatNotificationI[]> {
    const user = this.userHelperService.getSocketLoginData()!;

    let httpParams = new HttpParams().set('team', user.team.toString());

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }

    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    return firstValueFrom(
      this.http.get<chatNotificationI[]>(this.CHAT_BASE_URL + this.slug_unreaded_chats_last_count, {
        params: httpParams,
      })
    );
  }

  public getAllInQueueChats(
    { team, customerId, _id }: UserSocketLoginI,
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI = {} as ChatFilterI,
    customSort?,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams()
      .set('team', team.toString())
      .set('userId', _id)
      .set('customer', customerId)
      .set('page', page)
      .set('limit', limit)
      .set('inQueue', true);

    if (search) {
      httpParams = httpParams.set('search', search);
    }
    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }
    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user, {
      params: httpParams,
    });
  }

  /**
   * Lists chats already answered by the agent (status='attendance') — the inverse of the
   * "onlyWaiting" queue. Reuses the by-user endpoint, scoping the status server-side via statusList.
   */
  public getAllInAttendanceChats(
    { team, customerId, _id }: UserSocketLoginI,
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI = {} as ChatFilterI,
    customSort?,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams()
      .set('team', team.toString())
      .set('userId', _id)
      .set('customer', customerId)
      .set('page', page)
      .set('limit', limit)
      .set('inQueue', false)
      .set('statusList[0]', ChatStatusE.ATTENDANCE);

    if (search) {
      httpParams = httpParams.set('search', search);
    }
    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }
    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user, {
      params: httpParams,
    });
  }

  public getAllChatsForHistory(id: string): Observable<ChatI> {
    return this.http.get<ChatI>(this.CHAT_BASE_URL + this.slug_url_chats + id);
  }

  public getAllChats(teamId: string): Observable<ChatI[]> {
    return this.http.get<ChatI[]>(this.CHAT_BASE_URL + this.slug_url_by_user + `?team=${teamId}`);
  }

  public getAllMyChatsInAttendance(teamId: string, page = 1, limit = 10): Observable<PageI<ChatI>> {
    let params = new HttpParams({
      fromObject: { 'statusList[0]': ['attendance'], page, limit },
    });

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user + `?team=${teamId}`, { params });
  }

  public getAllMyChatsInAttendanceForKanban(
    teamId: string,
    page = 1,
    limit = 10,
    search = '',
    customSort?: object,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    return this.findByUser(
      [teamId],
      ['attendance'],
      page,
      limit,
      search,
      { notInKanban: true, teamOnly: this.isUserAdminLevel(), fromKanban: true },
      customSort,
      partial
    );
  }

  public getAllMyRecentChatsForKanban(
    teamId: string,
    page = 1,
    limit = 10,
    search = '',
    customSort?: object,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let params = new HttpParams({
      fromObject: {
        'statusList[0]': ChatStatusE.QUEUE,
        'statusList[1]': ChatStatusE.WAITING,
        page,
        limit,
        filter: JSON.stringify({ teamOnly: this.isUserAdminLevel(), fromKanban: true }),
      },
    });

    if (search) {
      params = params.set('search', search);
    }

    if (customSort) {
      params = params.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      params = params.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user + `?team=${teamId}`, { params });
  }

  public getAllChatsForKanbanCategory(
    categoryId: string,
    teamId: string | string[],
    page = 1,
    limit = 10,
    search = '',
    customSort?: object
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams()
      .set('team', teamId.toString())
      .set('statusList[0]', 'attendance')
      .set('page', page)
      .set('limit', limit)
      .set('filter', JSON.stringify({ teamOnly: this.isUserAdminLevel() }));

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    return this.http.get<PageI<ChatI>>(
      this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_find_by_kanban_category + categoryId,
      { params: httpParams }
    );
  }

  public getAllFinishedChats(team: string[], page = 1, limit = 10): Observable<PageI<ChatI>> {
    const params = new HttpParams({
      fromObject: { 'statusList[0]': ['finished'], page, limit },
    });
    params.append('team', JSON.stringify(team));
    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user + `?team=${team}`, { params });
  }

  public getAllFinishedChatsOnlyLast(
    options: { team: string[]; _id },
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI | any = {} as ChatFilterI,
    customSort?,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    const { team: userTeams, _id } = options;
    let httpParams = new HttpParams()
      .set('team', filter.team || userTeams.toString())
      .set('userId', _id)
      .set('page', page)
      .set('limit', limit);

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }

    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_last_contact_by_user, {
      params: httpParams,
    });
  }

  public getAllChatGroups(
    options: { team: string[]; _id },
    page = 1,
    limit = 30,
    search = '',
    filter: ChatFilterI = {} as ChatFilterI
  ): Observable<PageI<ChatGroupI>> {
    const { team, _id } = options;

    let httpParams = new HttpParams()
      .set('team', team.toString())
      .set('userId', _id)
      .set('page', page)
      .set('limit', limit);

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }

    return this.http.get<PageI<ChatGroupI>>(this.CHAT_BASE_URL + this.slug_url_group_by_user, {
      params: httpParams,
    });
  }

  public getAllArchivedChats(
    team: string[],
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI | any = {} as ChatFilterI,
    customSort?: object,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    return this.findByUser(team, ['archived'], page, limit, search, filter, customSort, partial);
  }

  public getAllApiChats(
    team: string[],
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI = {} as ChatFilterI,
    customSort?,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams()
      .set('team', team.toString())
      .set('statusList[0]', 'api')
      .set('page', page)
      .set('limit', limit);

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }

    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user, { params: httpParams });
  }

  public getAllDelayedChats(
    team: string[],
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI = {} as ChatFilterI,
    customSort?,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams()
      .set('team', team.toString())
      .set('statusList[0]', 'delayed')
      .set('page', page)
      .set('limit', limit);

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }

    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user, { params: httpParams });
  }

  public getAllFlowChats(
    team: string[],
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI = {} as ChatFilterI,
    customSort?: object,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams()
      .set('team', team.toString())
      .set('statusList[0]', 'start_flow')
      .set('statusList[1]', 'new')
      .set('statusList[2]', 'transfer')
      .set('page', page)
      .set('limit', limit);

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }
    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user, { params: httpParams });
  }

  public getAllWorkflowChats(
    team: string[],
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI = {} as ChatFilterI,
    customSort?,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams()
      .set('team', team.toString())
      .set('statusList[0]', 'workflow')
      .set('page', page)
      .set('limit', limit);

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }
    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user, { params: httpParams });
  }

  public getAllAIAssistedChats(
    team: string[],
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI = {} as ChatFilterI,
    customSort?,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams()
      .set('team', team.toString())
      .set('statusList[0]', 'ai_assisted')
      .set('page', page)
      .set('limit', limit);

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }
    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user, { params: httpParams });
  }

  private findByUser(
    team: string[],
    status: string[],
    page: number,
    limit: number,
    search: string,
    filter: ChatFilterI | any = {} as ChatFilterI,
    customSort?: object,
    partial?: boolean
  ): Observable<PageI<ChatI>> {
    let httpParams = new HttpParams().set('team', team.toString()).set('page', page).set('limit', limit);

    status.forEach((status, index) => {
      httpParams = httpParams.set(`statusList[${index}]`, status);
    });

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }

    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    if (partial) {
      httpParams = httpParams.set('partial', 'true');
    }

    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_by_user, { params: httpParams });
  }

  public setAllMessagesAsRead(chatId: string, type = SenderTypeE.CONTACT): Observable<any> {
    return this.http.patch(this.CHAT_BASE_URL + this.slug_url_read_message + chatId, {
      type,
    });
  }

  public createInternalChat(internalChat: InternalChatI): Observable<{ oldId: string; chat: ChatI }> {
    return this.http.post<{ oldId: string; chat: ChatI }>(
      this.CHAT_BASE_URL + this.slug_url_chats + 'internal-chat',
      internalChat
    );
  }

  public findInternalChats(): Observable<PageI<ChatI>> {
    return this.http.get<PageI<ChatI>>(this.CHAT_BASE_URL + this.slug_url_internal_chats);
  }

  public findChatByContact(contact: string, customerId: string) {
    return this.http.get<ChatI>(this.CHAT_BASE_URL + this.slug_url_by_contact + contact + `?customer=${customerId}`);
  }

  public findChatById(chatId: string) {
    return this.http.get<ChatI>(this.CHAT_BASE_URL + this.slug_url_chats + chatId);
  }

  public findOrCreateChat(chat: ChatI): Observable<ChatI> {
    return this.http.post<ChatI>(this.CHAT_BASE_URL + this.slug_url_chats, chat);
  }

  public findOrCreateWhatAppChat(chat: ChatI): Observable<ChatI> {
    return this.http.post<ChatI>(this.CHAT_BASE_URL + this.slug_url_chats + 'whatsapp-chat', chat);
  }

  public findOrCreateRcsChat(chat: ChatI): Observable<ChatI> {
    return this.http.post<ChatI>(this.CHAT_BASE_URL + this.slug_url_chats + 'rcs-chat', chat);
  }

  public findOrCreateWhatAppOficialChat(chat: ChatI): Observable<ChatI> {
    return this.http.post<ChatI>(`${this.CHAT_BASE_URL}${this.slug_url_chats}whatsapp-oficial-chat`, chat);
  }

  public createFacebookChat(chat: ChatI): Observable<ChatI> {
    return this.http.post<ChatI>(this.CHAT_BASE_URL + this.slug_url_chats + 'facebook-messenger-chat', chat);
  }

  public createInstagramChat(chat: ChatI): Observable<ChatI> {
    return this.http.post<ChatI>(this.CHAT_BASE_URL + this.slug_url_chats + 'instagram-direct-chat', chat);
  }

  public async updateMessageStatus(
    message: string,
    chat: string,
    status: string,
    chatStatus: ChatStatusT
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(this.CHAT_BASE_URL + this.slug_url_messages + 'update-message-status/' + message, {
        status,
        chat,
        chatStatus,
      })
    );
  }

  public async updateMessageDeletedStatus(
    ids: string[],
    jid: string,
    instanceKey?: string,
    omnismartInstance?: MessagesInteractionE
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(this.CHAT_BASE_URL + this.slug_url_messages + 'update-message-deleted-status/', {
        ids,
        jid,
        instanceKey,
        omnismartInstance,
      })
    );
  }

  public async updateScheduledMessage(
    messageId: string,
    jobId: string | number,
    chat: ChatI,
    message: Partial<MessageI>
  ): Promise<{ message: MessageI }> {
    return await firstValueFrom(
      this.http.patch<{ message: MessageI }>(
        this.CHAT_BASE_URL + this.slug_url_messages + 'scheduled/' + messageId + '/' + jobId,
        {
          chatStatus: chat.status,
          message,
          receiverRoom: chat._id,
        }
      )
    );
  }

  public async dropScheduledMessage(messageId: string, jobId: string | number): Promise<void> {
    await firstValueFrom(
      this.http.delete(this.CHAT_BASE_URL + this.slug_url_messages + 'scheduled/' + messageId + '/' + jobId)
    );
  }

  public transferChat(transfer: transferChatT): Observable<ChatI> {
    return this.http.post<ChatI>(environment.OMNISMART_API_FLOW_ENGINE + `transfers`, transfer);
  }

  public transferMultipleChats(transfers: transferChatT[]): Promise<{ jobId: string }> {
    return firstValueFrom(
      this.http.post<any>(environment.OMNISMART_API_FLOW_ENGINE + `transfers/multiple`, { transfers })
    );
  }

  public getFilterMessages(chatId: string, filter: string): Observable<MessageI[]> {
    return this.http.get<MessageI[]>(
      environment.OMNISMART_API_FLOW_ENGINE + this.slug_url_filter_message + chatId + `?filter=${filter}`
    );
  }

  setMessageSelected(message: MessageI): void {
    this.messageChatSelected.next(message);
  }

  getMessageSelected(): Observable<MessageI | undefined> {
    return this.messageChatSelected.asObservable();
  }

  transferChatMe(transfer: transferChatT) {
    return this.http.post<ChatI>(this.CHAT_BASE_URL + this.slug_url_chats + 'transfer/init-attendance', transfer);
  }

  getMyAttendaces(query: any): Observable<any> {
    return this.http.get<{ agentsInAttendance: string[] }>(
      `${environment.OMNISMART_API_ATTENDANCE_REPORTS}${this.slug_url_count_my_attendances}`,
      { params: query }
    );
  }

  checkChatsUnassigned(teams: string[]): Observable<any> {
    return this.http.get(`${this.CHAT_BASE_URL}${this.slug_url_chats_check_chats_unassigned}`, {
      params: {
        team: [...teams],
      },
    });
  }

  public updateChatMentions(chatId: string, mentions: ChatMentionI[]): Observable<any> {
    return this.http.patch(this.CHAT_BASE_URL + this.slug_url_chats + this.slug_url_update_chat_mentions + chatId, {
      mentions,
    });
  }

  public createStepCallInitiated(chatId: string, room: string): Observable<ChatStepI> {
    return this.http.patch<ChatStepI>(`${this.CHAT_BASE_URL}chat/${chatId}/create-step-call-initiated`, {
      type: 'callInitiated',
      createdAt: new Date(),
      agentSupervisor: false,
      room,
    });
  }

  public createStepCallEnded(chatId: string, room: string): Observable<ChatStepI> {
    return this.http.patch<ChatStepI>(`${this.CHAT_BASE_URL}chat/${chatId}/create-step-call-ended`, {
      type: 'callEnded',
      createdAt: new Date(),
      agentSupervisor: false,
      room,
    });
  }

  public sendSMSMessage(message: MessageI) {
    return this.http.post(`${this.CHAT_BASE_URL}${this.slug_url_sms}`, message);
  }

  public createEmailSendHistory(email: MailHistoryI) {
    return this.http.post(`${this.CHAT_BASE_URL}${this.slug_url_email_history}`, email);
  }

  public isUserAdminLevel(): boolean {
    return this.userHelperService.currentLevel() >= ProfileLevel.HIGH;
  }

  public chatInitLock(transfer: transferChatT) {
    return this.http.post<boolean>(`${this.CHAT_BASE_URL}${this.slug_url_chats}transfer/chat-init/lock`, transfer);
  }

  public async loadAllFinishedChatsOnlyLastCount(
    options: { team: string[]; _id },
    page = 1,
    limit = 10,
    search = '',
    filter: ChatFilterI | any = {} as ChatFilterI,
    customSort?
  ) {
    const value = this.finishedChatsCountSubject.value;

    if (value !== null) {
      //already loaded
      return;
    }

    const { team: userTeams, _id } = options;

    let httpParams = new HttpParams()
      .set('team', filter.team || userTeams.toString())
      .set('userId', _id)
      .set('page', page)
      .set('limit', limit);

    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }

    if (customSort) {
      httpParams = httpParams.set('customSort', JSON.stringify(customSort));
    }

    const result = await firstValueFrom(
      this.http.get<any>(this.CHAT_BASE_URL + this.slug_url_last_contact_by_user + 'count', {
        params: httpParams,
      })
    );

    this.finishedChatsCountSubject.next(result);
    return result;
  }

  public async exportChatsToPDF(chatIds: string[]): Promise<{ jobId: string }> {
    return await firstValueFrom(
      this.http.get<{ jobId: string }>(
        `${this.ATTENDANCE_BASE_URL}${this.slug_url_attendance_pdf_exports}export-chats`,
        {
          params: new HttpParams().set('chatIds', chatIds.join(',')),
        }
      )
    );
  }
}
